import { useEffect, useRef, useState } from 'react';
import type { ContactListItem } from '@cockpit/shared';
import { api } from '../../lib/api';

interface Props {
  value: ContactListItem | null;
  onChange: (contact: ContactListItem | null) => void;
  onCreateNew?: (queryText: string) => void;
  placeholder?: string;
  required?: boolean;
}

export function ContactAutocomplete({
  value,
  onChange,
  onCreateNew,
  placeholder = 'Rechercher un client…',
  required,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [items, setItems] = useState<ContactListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click extérieur ferme la liste.
  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (wrapperRef.current !== null && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return (): void => {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  // Recherche debounced.
  useEffect(() => {
    if (text.trim().length < 2) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api.contacts
        .list({ q: text, page_size: 10 })
        .then((res) => {
          if (!cancelled) setItems(res.items);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return (): void => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [text]);

  const handleSelect = (c: ContactListItem): void => {
    onChange(c);
    setText('');
    setOpen(false);
  };

  if (value !== null) {
    return (
      <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100">
        <span className="flex-1 truncate">
          <span className="font-medium">{value.nom}</span>
          {value.societe !== null && <span className="text-slate-400"> — {value.societe}</span>}
        </span>
        <button
          type="button"
          onClick={(): void => onChange(null)}
          className="text-slate-400 hover:text-slate-200 text-xs"
        >
          Changer
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={text}
        onChange={(e): void => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={(): void => setOpen(true)}
        placeholder={placeholder}
        required={required && value === null}
        className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-floryon-green focus:outline-none focus:ring-1 focus:ring-floryon-green/50"
      />
      {open && text.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 w-full bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-slate-500">Recherche…</div>}
          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">Aucun résultat.</div>
          )}
          {items.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={(): void => handleSelect(c)}
              className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              <div className="font-medium">{c.nom}</div>
              {c.societe !== null && <div className="text-xs text-slate-500">{c.societe}</div>}
            </button>
          ))}
          {onCreateNew !== undefined && (
            <button
              type="button"
              onClick={(): void => {
                onCreateNew(text);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-floryon-green hover:bg-slate-800 border-t border-slate-700"
            >
              + Créer "{text}" comme nouveau contact
            </button>
          )}
        </div>
      )}
    </div>
  );
}
